// src/features/dashboard/superadmin/OrganisationFormInline.jsx
import React, { useState } from 'react';
import { CreateSpreadsheetButton } from './CreateSpreadsheetButton';
import { SHEET_IDS } from '../../../core/config';

const MASTER_SHEET_ID = SHEET_IDS.MASTER;

export const OrganisationFormInline = ({
  org,
  onSubmit,
  onCancel,
  existingOrganisationNames = [] // 👈 receive list of existing names
}) => {
  const isEditing = !!org;

  const generateOrgId = () => {
    const now = Date.now();
    const random = Math.random().toString(36).substring(2, 6);
    return `ORG_${now}_${random}`;
  };

  const [formData, setFormData] = useState({
    organisationId: org?.organisationId || generateOrgId(),
    organisationName: org?.organisationName || '',
    organisationCode: org?.organisationCode || '',
    spreadsheetId: org?.spreadsheetId || '',
    status: org?.status || 'active',
    usersSpreadsheetId: org?.usersSpreadsheetId || '',
    attendanceSpreadsheetId: org?.attendanceSpreadsheetId || '',
    marksSpreadsheetId: org?.marksSpreadsheetId || '',
    topicsSpreadsheetId: org?.topicsSpreadsheetId || '',
    adminName: '',
    adminSurname: '',
    adminEmail: '',
    adminGender: '',
    adminSchool: '',
    adminProvince: '',
    adminDistrict: '',
    adminCircuit: '',
  });

  // 👇 NEW: state for bulk status update (only used when editing)
  const [bulkStatus, setBulkStatus] = useState('');

  const handleSpreadsheetCreated = (ids) => {
    setFormData(prev => ({
      ...prev,
      spreadsheetId: ids.spreadsheetId || ids.users || '',
      usersSpreadsheetId: ids.users || '',
      attendanceSpreadsheetId: ids.attendance || '',
      marksSpreadsheetId: ids.marks || '',
      topicsSpreadsheetId: ids.topics || '',
    }));
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!isEditing && (!formData.adminName || !formData.adminSurname || !formData.adminEmail)) {
      alert('Please fill in the initial admin details (Name, Surname, Email).');
      return;
    }
    if (!isEditing && !formData.spreadsheetId) {
      alert('Please create a spreadsheet or provide a Spreadsheet ID.');
      return;
    }
    const adminData = {
      name: formData.adminName,
      surname: formData.adminSurname,
      email: formData.adminEmail,
      gender: formData.adminGender || '',
      school: formData.adminSchool || 'N/A',
      province: formData.adminProvince || 'N/A',
      district: formData.adminDistrict || 'N/A',
      circuit: formData.adminCircuit || 'N/A',
    };
    // 👇 Pass bulkStatus along with the rest
    onSubmit({ ...formData, adminData, bulkStatus });
  };

  const existingIds = {
    users: formData.usersSpreadsheetId,
    attendance: formData.attendanceSpreadsheetId,
    marks: formData.marksSpreadsheetId,
    topics: formData.topicsSpreadsheetId,
  };

  // 👇 Duplicate check logic
  const isNameDuplicate = (name) => {
    if (!name.trim()) return false;
    const nameLower = name.trim().toLowerCase();
    // If editing, exclude the current organisation's own name
    if (isEditing && org?.organisationName?.toLowerCase() === nameLower) {
      return false;
    }
    return existingOrganisationNames.some(existing => existing === nameLower);
  };

  const currentName = formData.organisationName.trim();
  const duplicateError = isNameDuplicate(currentName);

  return (
    <div className="bg-white rounded-lg shadow p-6 mb-6">
      <h2 className="text-2xl font-bold mb-4">{isEditing ? 'Edit Organisation' : 'Add Organisation'}</h2>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          {/* Organisation ID – auto‑generated, read‑only */}
          <div>
            <label className="block text-sm font-medium">Organisation ID</label>
            <input
              type="text"
              name="organisationId"
              value={formData.organisationId}
              readOnly
              className="w-full border rounded px-3 py-2 bg-gray-100 cursor-not-allowed"
            />
          </div>
          <div>
            <label className="block text-sm font-medium">Organisation Name *</label>
            <input
              type="text"
              name="organisationName"
              value={formData.organisationName}
              onChange={handleChange}
              className={`w-full border rounded px-3 py-2 ${duplicateError ? 'border-red-500' : ''}`}
              required
            />
            {duplicateError && (
              <p className="text-red-500 text-sm mt-1">An organisation with this name already exists.</p>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium">Organisation Code</label>
            <input
              type="text"
              name="organisationCode"
              value={formData.organisationCode}
              onChange={handleChange}
              className="w-full border rounded px-3 py-2"
            />
          </div>
          <div>
            <label className="block text-sm font-medium">Status</label>
            <select
              name="status"
              value={formData.status}
              onChange={handleChange}
              className="w-full border rounded px-3 py-2"
            >
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>

          {/* Master Spreadsheet ID – auto‑filled, read‑only */}
          <div className="col-span-2">
            <label className="block text-sm font-medium">Master Spreadsheet ID</label>
            <input
              type="text"
              value={MASTER_SHEET_ID}
              readOnly
              className="w-full border rounded px-3 py-2 bg-gray-100 cursor-not-allowed"
            />
          </div>

          {/* Create Missing Spreadsheets Button */}
          <div className="col-span-2 border-t pt-3">
            <div className="flex flex-wrap items-end gap-2">
              <div className="flex-1">
                <label className="block text-sm font-medium">Spreadsheet IDs</label>
                <p className="text-xs text-gray-500">
                  Click the button to create only the missing spreadsheets. Already filled IDs will be skipped.
                </p>
              </div>
              <CreateSpreadsheetButton
                organisationName={formData.organisationName}
                existingIds={existingIds}
                onSpreadsheetCreated={handleSpreadsheetCreated}
                disabled={!!duplicateError} // 👈 Disable if name is duplicate
              />
            </div>
          </div>

          {/* Individual Spreadsheet IDs – editable */}
          <div>
            <label className="block text-sm font-medium">Users Spreadsheet ID</label>
            <input
              type="text"
              name="usersSpreadsheetId"
              value={formData.usersSpreadsheetId}
              onChange={handleChange}
              className="w-full border rounded px-3 py-2"
              placeholder="Auto‑filled"
            />
          </div>
          <div>
            <label className="block text-sm font-medium">Attendance Spreadsheet ID</label>
            <input
              type="text"
              name="attendanceSpreadsheetId"
              value={formData.attendanceSpreadsheetId}
              onChange={handleChange}
              className="w-full border rounded px-3 py-2"
              placeholder="Auto‑filled"
            />
          </div>
          <div>
            <label className="block text-sm font-medium">Marks Spreadsheet ID</label>
            <input
              type="text"
              name="marksSpreadsheetId"
              value={formData.marksSpreadsheetId}
              onChange={handleChange}
              className="w-full border rounded px-3 py-2"
              placeholder="Auto‑filled"
            />
          </div>
          <div>
            <label className="block text-sm font-medium">Topics Spreadsheet ID</label>
            <input
              type="text"
              name="topicsSpreadsheetId"
              value={formData.topicsSpreadsheetId}
              onChange={handleChange}
              className="w-full border rounded px-3 py-2"
              placeholder="Auto‑filled"
            />
          </div>

          {/* Main Spreadsheet ID (fallback) */}
          <div className="col-span-2">
            <label className="block text-sm font-medium">Main Spreadsheet ID (Fallback)</label>
            <input
              type="text"
              name="spreadsheetId"
              value={formData.spreadsheetId}
              onChange={handleChange}
              className="w-full border rounded px-3 py-2"
              placeholder="Auto‑filled from Users ID"
            />
            <p className="text-xs text-gray-500 mt-1">
              This is used as a fallback. Usually set to the Users spreadsheet ID.
            </p>
          </div>
        </div>

        {/* 👇 NEW: Bulk user status update section (only visible when editing) */}
        {isEditing && (
          <div className="col-span-2 border-t pt-4 mt-2">
            <h4 className="font-medium mb-2">👥 Bulk Update All User Statuses</h4>
            <div className="flex flex-wrap items-center gap-4">
              <label htmlFor="bulkStatus" className="text-sm text-gray-600">
                Set all members to:
              </label>
              <select
                id="bulkStatus"
                value={bulkStatus}
                onChange={(e) => setBulkStatus(e.target.value)}
                className="border border-gray-300 rounded-md px-3 py-2 text-sm bg-white shadow-sm focus:ring-purple-500 focus:border-purple-500"
              >
                <option value="">— Don’t change —</option>
                <option value="active">🟢 Active</option>
                <option value="partial">🟡 Partial</option>
                <option value="inactive">🔴 Inactive</option>
              </select>
              <span className="text-xs text-gray-400">
                (This will override the status for <strong>every</strong> user in this organisation)
              </span>
            </div>
          </div>
        )}

        {!isEditing && (
          <div className="border-t pt-4">
            <h4 className="font-medium mb-2">Initial Admin for this Organisation</h4>
            <div className="grid grid-cols-2 gap-4">
              {/* Admin fields – unchanged */}
              <div>
                <label className="block text-sm font-medium">Admin Name *</label>
                <input
                  type="text"
                  name="adminName"
                  value={formData.adminName}
                  onChange={handleChange}
                  className="w-full border rounded px-3 py-2"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium">Admin Surname *</label>
                <input
                  type="text"
                  name="adminSurname"
                  value={formData.adminSurname}
                  onChange={handleChange}
                  className="w-full border rounded px-3 py-2"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium">Admin Email *</label>
                <input
                  type="email"
                  name="adminEmail"
                  value={formData.adminEmail}
                  onChange={handleChange}
                  className="w-full border rounded px-3 py-2"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium">Admin Gender</label>
                <select
                  name="adminGender"
                  value={formData.adminGender}
                  onChange={handleChange}
                  className="w-full border rounded px-3 py-2"
                >
                  <option value="">Select</option>
                  <option value="Male">Male</option>
                  <option value="Female">Female</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium">Admin School</label>
                <input
                  type="text"
                  name="adminSchool"
                  value={formData.adminSchool}
                  onChange={handleChange}
                  className="w-full border rounded px-3 py-2"
                  placeholder="N/A"
                />
              </div>
              <div>
                <label className="block text-sm font-medium">Admin Province</label>
                <input
                  type="text"
                  name="adminProvince"
                  value={formData.adminProvince}
                  onChange={handleChange}
                  className="w-full border rounded px-3 py-2"
                  placeholder="N/A"
                />
              </div>
              <div>
                <label className="block text-sm font-medium">Admin District</label>
                <input
                  type="text"
                  name="adminDistrict"
                  value={formData.adminDistrict}
                  onChange={handleChange}
                  className="w-full border rounded px-3 py-2"
                  placeholder="N/A"
                />
              </div>
              <div>
                <label className="block text-sm font-medium">Admin Circuit</label>
                <input
                  type="text"
                  name="adminCircuit"
                  value={formData.adminCircuit}
                  onChange={handleChange}
                  className="w-full border rounded px-3 py-2"
                  placeholder="N/A"
                />
              </div>
            </div>
          </div>
        )}

        <div className="flex justify-end gap-2 pt-4">
          <button type="button" onClick={onCancel} className="px-4 py-2 bg-gray-300 rounded hover:bg-gray-400">
            Cancel
          </button>
          <button
            type="submit"
            disabled={!!duplicateError}
            className={`px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 ${
              duplicateError ? 'opacity-50 cursor-not-allowed' : ''
            }`}
          >
            {isEditing ? 'Update Organisation' : 'Create Organisation'}
          </button>
        </div>
      </form>
    </div>
  );
};